import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Plus, LogIn, Skull, X } from 'lucide-react'
import { Badge } from '@/shared/components/ui/Badge'
import { Button, buttonClass } from '@/shared/components/ui/Button'
import { EmptyState } from '@/shared/components/ui/EmptyState'
import { InfoButton } from '@/shared/components/ui/InfoButton'
import { Section } from '@/shared/components/ui/Section'
import { Skeleton } from '@/shared/components/ui/Skeleton'
import { errorMessage } from '@/shared/i18n/errorMessage'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { useUserSurvivorGames, useJoinSurvivorGame } from '../queries'
import { SurvivorGameStatus, type SurvivorGameListItem } from '../types'
import { SurvivorRulesSheet } from './SurvivorRulesSheet'
import styles from './SobreviventeListPage.module.css'

const CODE_REGEX = /^[A-Z0-9]{3}-[A-Z0-9]{4}$/

function formatCode(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7)
  if (clean.length <= 3) return clean
  return `${clean.slice(0, 3)}-${clean.slice(3)}`
}

export function SobreviventeListPage() {
  const { t } = useTranslation()
  const { data: games, isPending, isError } = useUserSurvivorGames()

  return (
    <div className={styles.page}>
      <QuickActions />

      {isPending && (
        <div className={styles.skeleton}>
          <Skeleton height="64px" />
          <Skeleton height="64px" />
        </div>
      )}

      {isError && (
        <p className={styles.errorBox} role="alert">{t('survivor.list.error')}</p>
      )}

      {!isPending && games && games.length === 0 && (
        <EmptyState
          icon={<Skull size={32} />}
          title={t('survivor.list.emptyTitle')}
          description={t('survivor.list.emptyDescription')}
          action={
            <Link to="/sobrevivente/new" className={buttonClass({ variant: 'primary' })}>
              <Plus size={16} aria-hidden="true" />
              {t('nav.createGame')}
            </Link>
          }
        />
      )}

      {games && games.length > 0 && (
        <Section label={t('home.myGames')} meta={games.length}>
          {games.map((game) => (
            <GameRow key={game.id} game={game} />
          ))}
        </Section>
      )}
    </div>
  )
}

// ── Create + join with a code ─────────────────────────────────────────────────

function QuickActions() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showJoin, setShowJoin]   = useState(false)
  const [showRules, setShowRules] = useState(false)
  const [code, setCode]           = useState('')
  const [formatError, setFormatError] = useState('')

  const joinMutation = useJoinSurvivorGame()
  const isCodeValid  = CODE_REGEX.test(code)

  // The page used to carry its own switch over error codes; errorMessage() is
  // the one mapping now, so a new code never renders as an empty string.
  const displayedError =
    formatError || (joinMutation.error ? errorMessage(joinMutation.error, t) : '')

  function handleJoin(e: React.FormEvent) {
    e.preventDefault()
    if (!isCodeValid) {
      setFormatError(t('survivor.list.codeFormat'))
      return
    }
    setFormatError('')
    joinMutation.mutate(code, {
      onSuccess: (result) => {
        setCode('')
        setShowJoin(false)
        void navigate({
          to:     '/sobrevivente/$gameId',
          params: { gameId: result.gameId },
          search: { gw: undefined },
        })
      },
    })
  }

  return (
    <div className={styles.actions}>
      <div className={styles.actionsRow}>
        <Link to="/sobrevivente/new" className={buttonClass({ variant: 'primary' })}>
          <Plus size={16} aria-hidden="true" />
          {t('nav.createGame')}
        </Link>

        {!showJoin ? (
          <Button variant="secondary" onClick={() => setShowJoin(true)}>
            <LogIn size={16} aria-hidden="true" />
            {t('nav.joinWithCode')}
          </Button>
        ) : (
          <form className={styles.joinForm} onSubmit={handleJoin} noValidate>
            <input
              className={styles.joinInput}
              type="text"
              value={code}
              onChange={(e) => {
                setCode(formatCode(e.target.value))
                if (formatError) setFormatError('')
              }}
              placeholder="XXX-XXXX"
              maxLength={8}
              autoComplete="off"
              spellCheck={false}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              aria-label={t('survivor.list.joinCode')}
              aria-invalid={displayedError !== '' || undefined}
            />
            <Button type="submit" isLoading={joinMutation.isPending} disabled={!isCodeValid}>
              {t('survivor.list.join')}
            </Button>
            <Button
              variant="ghost"
              isIconOnly
              aria-label={t('common.cancel')}
              onClick={() => {
                setShowJoin(false)
                setCode('')
                setFormatError('')
                joinMutation.reset()
              }}
            >
              <X size={16} aria-hidden="true" />
            </Button>
          </form>
        )}

        <InfoButton label={t('survivor.game.rules')} onClick={() => setShowRules(true)} />
      </div>

      {showJoin && displayedError && (
        <span className={styles.joinError} role="alert">{displayedError}</span>
      )}

      {showRules && <SurvivorRulesSheet onClose={() => setShowRules(false)} />}
    </div>
  )
}

// ── One game ──────────────────────────────────────────────────────────────────

function GameRow({ game }: { game: SurvivorGameListItem }) {
  const { t } = useTranslation()

  const isFinished = game.status === SurvivorGameStatus.Finished
  const statusLabel =
    game.status === SurvivorGameStatus.RegistrationOpen ? t('survivor.status.waiting')
    : game.status === SurvivorGameStatus.Active         ? t('survivor.status.inProgress')
                                                        : t('survivor.status.finished')

  return (
    <Link
      to="/sobrevivente/$gameId"
      params={{ gameId: game.id }}
      search={{ gw: undefined }}
      className={styles.row}
    >
      <span className={styles.rowMain}>
        <span className={styles.name}>{game.name}</span>
        <span className={styles.meta}>
          {game.competitionName} · {statusLabel}
          {/* Suppressed on season 1 — that is every game that has never been restarted. */}
          {game.seasonNumber > 1
            && <> · {t('survivor.season.short', { number: game.seasonNumber })}</>}
        </span>
      </span>

      <span className={styles.rowSide}>
        <span className={styles.aliveCount}>
          {game.aliveCount}
          <span className={styles.aliveOf}>/{game.participantCount}</span>
        </span>
        {game.isChampion ? (
          <Badge tone="money" variant="soft">{t('survivor.status.champion')}</Badge>
        ) : !game.isAlive ? (
          <Badge tone="out" variant="soft">{t('survivor.status.eliminated')}</Badge>
        ) : !isFinished ? (
          <Badge tone="alive" variant="outline" hasDot>{t('survivor.status.alive')}</Badge>
        ) : null}
      </span>
    </Link>
  )
}
